import React, {useState, useEffect, useRef} from "react";
import {useDrag, useDrop} from 'react-dnd'
import { ItemTypes } from './itemTypes.js';
import { findObjectByKeyValue } from '../utils.js';
import { Spinner } from "../common/Spinner";
import { ErrorMessage } from "../common/ErrorMessage";
import PlayerCardMini from "../cards/PlayerCardMini.jsx";

function FieldPlayerSlot({
    position,
    index,
    fieldWidth,
    topGap,
    rowHeight,
    columnGap,
    activePlayers,
    removePlayer,
    touchPlayer,
    touchedPlayer,
    updatePlayerInfo,
    onSlotTap,
    display
}) {
    const effectiveCols = position.cols === "5" ? 5 : 4;
    const playerWidth = (fieldWidth - columnGap * (effectiveCols + 1)) / effectiveCols;
    const columnWidth = 100 / position.cols;
    const leftPos = columnWidth * position.col - columnWidth / 2;
    const topPos = topGap + rowHeight * (position.row - 1);
    const itemMargin = `0 0 0 -${playerWidth / 2}px`;
    // Same breakpoint the stylesheet uses for the mobile team page layout,
    // read at render time so a resize crossing it never leaves a stale value
    // (the ResizeObserver below re-renders on every size change).
    const isDesktopViewport = typeof window !== 'undefined'
        && typeof window.matchMedia === 'function'
        && window.matchMedia('(min-width: 961px)').matches;
    // Desktop cards grow up to 19px, but a card is 7.8em tall and pitch rows
    // sit one rowHeight apart, so cap the size to the row band (pitch height
    // comes from the field.svg 458x684 aspect ratio) minus a 5px gap. That
    // keeps defender cards clear of the GK card in the row below them.
    const rowBandPx = fieldWidth * (684 / 458) * (rowHeight / 100);
    const desktopFontCap = Math.max(Math.min(19, (rowBandPx - 5) / 7.8), 7);
    const cardFontSize = isDesktopViewport
        ? Math.min(Math.max(playerWidth * 0.16, 7), desktopFontCap)
        : Math.min(Math.max(playerWidth * 0.13, 7), 16);

    const slotData = findObjectByKeyValue(activePlayers || [], 'id', index);
    const playerInfo = slotData?.player_info || null;
    const activeClass = playerInfo ? " active " : "";
    const touchedClass = playerInfo && touchedPlayer && touchedPlayer.wyId === playerInfo.wyId ? " touched " : "";

    const [, drop] = useDrop(() => ({
        accept: ItemTypes.PLAYER,
        drop: (item) => {
            console.log(`[FieldPlayers Drop] Dropped item onto index ${index}:`, item);
            if (item && item.playerInfo && updatePlayerInfo) {
                updatePlayerInfo(index, item.playerInfo);
            } else {
                console.warn("[FieldPlayers Drop] Drop ignored: Missing item, playerInfo, or updatePlayerInfo handler.");
            }
        },
    }), [index, updatePlayerInfo]);

    const [{ isDragging }, drag] = useDrag(() => ({
        type: ItemTypes.PLAYER,
        item: { playerInfo },
        canDrag: !!playerInfo,
        collect: (monitor) => ({
            isDragging: !!monitor.isDragging(),
        }),
    }), [playerInfo]);

    const setSlotRef = React.useCallback((node) => {
        drag(drop(node));
    }, [drag, drop]);

    const handleTouch = (event) => {
        event.stopPropagation();
        // Mobile flow: the page opens the slot picker sheet instead of the
        // desktop select-then-place toggle.
        if (onSlotTap) {
            onSlotTap(index);
            return;
        }
        if (!touchPlayer) return;
        if (playerInfo) {
            touchPlayer(playerInfo);
        } else {
            touchPlayer({ isEmptySlot: true, id: index });
        }
    };

    return (
        <div
            ref={setSlotRef}
            className={"player-place-item placed " + activeClass + touchedClass}
            style={{
                left: leftPos + '%',
                width: playerWidth + 'px',
                top: topPos + '%',
                margin: itemMargin,
                display,
                opacity: isDragging ? 0.5 : 1,
                cursor: playerInfo ? 'grab' : 'default',
            }}
            onClick={handleTouch}
        >
            {playerInfo ? (
                <div className="team-pitch-card" style={{ fontSize: `${cardFontSize}px` }}>
                    <PlayerCardMini
                        player={{ ...playerInfo, position: position.position }}
                        showRating
                    />
                    <button
                        type="button"
                        className="pp-clear-btn"
                        onClick={(event) => {
                            event.stopPropagation();
                            if (playerInfo?.wyId) {
                                removePlayer(playerInfo.wyId);
                            }
                        }}
                    />
                </div>
            ) : (
                <div className="team-pitch-card" style={{ fontSize: `${cardFontSize}px` }}>
                    <PlayerCardMini
                        emptySlot
                        player={{ position: position.position }}
                    />
                </div>
            )}
        </div>
    );
}

function FieldPlayersGrid({props, activePlayers, removePlayer, touchPlayer, touchedPlayer, updatePlayerInfo, onSlotTap}) {
    const topGap = 3;
    const numOfRows = 6.1;
    const columnGap = 10;
    const rowHeight = (100 - topGap) / numOfRows;
    const positions = props.positions;
    const fieldRef = useRef(null);
    const [fieldWidth, setFieldWidth] = useState(0);
    const [display, setDisplay] = useState('none');

    useEffect(() => {
        const element = fieldRef.current;
        if (!element) return undefined;

        const updateWidth = () => {
            setFieldWidth(element.offsetWidth);
            setDisplay('block');
        };

        updateWidth();

        if (typeof ResizeObserver === 'undefined') {
            return undefined;
        }

        const resizeObserver = new ResizeObserver(updateWidth);
        resizeObserver.observe(element);

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    useEffect(() => {
        if (!touchedPlayer || !touchPlayer) return undefined;

        const close = () => {
            touchPlayer(null);
        };

        window.addEventListener("click", close);
        return () => {
            window.removeEventListener("click", close);
        };
    }, [touchPlayer, touchedPlayer]);

    return (
        <div ref={fieldRef} id="player_places" className="players-places">
            {positions.map((position, index) => (
                <FieldPlayerSlot
                    key={position.id || index}
                    position={position}
                    index={index}
                    fieldWidth={fieldWidth}
                    topGap={topGap}
                    rowHeight={rowHeight}
                    columnGap={columnGap}
                    activePlayers={activePlayers}
                    removePlayer={removePlayer}
                    touchPlayer={touchPlayer}
                    touchedPlayer={touchedPlayer}
                    updatePlayerInfo={updatePlayerInfo}
                    onSlotTap={onSlotTap}
                    display={display}
                />
            ))}
        </div>
    );
}

function FieldPlayers({props, activePlayers, removePlayer, touchPlayer, touchedPlayer, updatePlayerInfo, onSlotTap, isLoading, error}) {
    if (isLoading) {
        return (
            <div className="players-places loading-container">
                <Spinner />
                <p>Loading field positions...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="players-places">
                <ErrorMessage message="Error loading field positions" error={error} />
            </div>
        );
    }

    if (!props || !props.positions || props.positions.length === 0) {
        return (
            <div className="players-places empty-state">
                <h3>No field positions available</h3>
                <p>Formation data could not be loaded.</p>
            </div>
        );
    }

    return (
        <FieldPlayersGrid
            props={props}
            activePlayers={activePlayers}
            removePlayer={removePlayer}
            touchPlayer={touchPlayer}
            touchedPlayer={touchedPlayer}
            updatePlayerInfo={updatePlayerInfo}
            onSlotTap={onSlotTap}
        />
    );
}

export {FieldPlayers}
